import React, { useState, useEffect } from 'react';
import {
  Card,
  List,
  Button,
  Space,
  Select,
  Input,
  App,
  Tag,
  Empty,
  Typography
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  FileTextOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import logService from '../../services/logService';

const { Option } = Select;
const { Text } = Typography;

export default function SystemLogs() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [logFiles, setLogFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [logLines, setLogLines] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [lineCount, setLineCount] = useState(100);

  useEffect(() => {
    loadLogFiles();
  }, []);

  useEffect(() => {
    if (selectedFile) {
      loadLogContent();
    }
  }, [selectedFile, searchTerm, lineCount]);

  const loadLogFiles = async () => {
    try {
      const response = await logService.getSystemLogFiles();

      if (response.success) {
        setLogFiles(response.data);
        if (response.data.length > 0 && !selectedFile) {
          setSelectedFile(response.data[0].name);
        }
      } else {
        message.error('加载日志文件列表失败');
      }
    } catch (error) {
      console.error('Failed to load log files:', error);
      message.error('加载日志文件列表失败');
    }
  };

  const loadLogContent = async () => {
    if (!selectedFile) return;

    setLoading(true);
    try {
      const response = await logService.getSystemLog({
        file: selectedFile,
        lines: lineCount,
        search: searchTerm || undefined
      });

      if (response.success) {
        setLogLines(response.data.lines);
      } else {
        message.error(response.data.error || '加载日志内容失败');
      }
    } catch (error) {
      console.error('Failed to load log content:', error);
      message.error('加载日志内容失败');
    } finally {
      setLoading(false);
    }
  };

  const [downloading, setDownloading] = useState(false);

  const handleDownloadLog = async () => {
    if (!selectedFile) return;

    setDownloading(true);
    try {
      // 后端 lines 上限为 500 行，通过带鉴权的 apiClient 拉取内容后在前端触发下载
      const response = await logService.getSystemLog({
        file: selectedFile,
        lines: 500
      });

      if (!response.success) {
        message.error(response.data?.error || '下载日志失败');
        return;
      }

      const content = (response.data.lines || []).join('\n');
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedFile}-${dayjs().format('YYYYMMDD-HHmmss')}.log`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success(`已下载最近 ${response.data.total_lines || 0} 行日志`);
    } catch (error) {
      console.error('Failed to download log:', error);
      message.error('下载日志失败');
    } finally {
      setDownloading(false);
    }
  };

  const getLogLevel = (line) => {
    if (!line) return null;

    if (line.includes('[ERROR]') || line.includes('ERROR')) {
      return { level: 'ERROR', color: '#f5222d' };
    } else if (line.includes('[WARNING]') || line.includes('WARNING')) {
      return { level: 'WARNING', color: '#fa8c16' };
    } else if (line.includes('[INFO]') || line.includes('INFO')) {
      return { level: 'INFO', color: '#1890ff' };
    } else if (line.includes('[DEBUG]') || line.includes('DEBUG')) {
      return { level: 'DEBUG', color: '#52c41a' };
    }

    return null;
  };

  const highlightSearch = (line) => {
    if (!searchTerm) return line;

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = line.split(regex);

    return parts.map((part, index) => {
      if (regex.test(part)) {
        return (
          <span key={index} style={{ background: '#ffe58f', fontWeight: 'bold' }}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div>
      <Card
        title="系统日志"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadLogContent} loading={loading}>
              刷新
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadLog}
              disabled={!selectedFile}
              loading={downloading}
            >
              下载
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical">
          <Space wrap>
            <Select
              style={{ width: 350 }}
              placeholder="选择日志文件"
              value={selectedFile}
              onChange={setSelectedFile}
              suffixIcon={<FileTextOutlined />}
            >
              {logFiles.map((file) => (
                <Option key={file.name} value={file.name} disabled={!file.readable}>
                  <Space>
                    <span>{file.name}</span>
                    <Tag color={file.readable ? 'default' : 'red'}>
                      {file.size_mb ? `${file.size_mb}MB` : formatFileSize(file.size)}
                    </Tag>
                    {!file.readable && <Tag color="red">文件过大</Tag>}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(file.modified).format('YYYY-MM-DD HH:mm')}
                    </Text>
                  </Space>
                </Option>
              ))}
            </Select>

            <Input
              placeholder="搜索日志内容"
              style={{ width: 250 }}
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />

            <Select
              style={{ width: 150 }}
              value={lineCount}
              onChange={setLineCount}
            >
              <Option value={50}>最近 50 行</Option>
              <Option value={100}>最近 100 行</Option>
              <Option value={200}>最近 200 行</Option>
              <Option value={500}>最近 500 行</Option>
              <Option value={1000}>最近 1000 行</Option>
            </Select>
          </Space>
        </Space>

        {logLines.length === 0 ? (
          <Empty description="暂无日志内容" />
        ) : (
          <div
            style={{
              background: '#1e1e1e',
              color: '#d4d4d4',
              padding: 16,
              borderRadius: 4,
              maxHeight: '70vh',
              overflow: 'auto',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: 13,
              lineHeight: 1.6
            }}
          >
            {logLines.map((line, index) => {
              const logLevel = getLogLevel(line);

              return (
                <div key={index} style={{ marginBottom: 2 }}>
                  {logLevel && (
                    <Tag
                      color={logLevel.color}
                      style={{
                        marginRight: 8,
                        fontSize: 11,
                        padding: '0 4px',
                        lineHeight: '18px'
                      }}
                    >
                      {logLevel.level}
                    </Tag>
                  )}
                  <span
                    style={{
                      color: logLevel
                        ? logLevel.level === 'ERROR'
                          ? '#f48771'
                          : logLevel.level === 'WARNING'
                          ? '#dcdcaa'
                          : '#d4d4d4'
                        : '#d4d4d4'
                    }}
                  >
                    {highlightSearch(line)}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 16, color: '#666', fontSize: 12 }}>
          显示 {logLines.length} 行日志
          {searchTerm && ` (筛选: "${searchTerm}")`}
        </div>
      </Card>
    </div>
  );
}
